import React, {useEffect, useState} from 'react';
import {Badge, Card, Divider} from 'antd-mobile';
import {useNavigate} from 'react-router-dom';
import {ExceptionDictionaryType} from "../graphql/queries/getExceptionDictionaryForDelivery";
import {t} from "../../../i18n";
import {useTranslation} from "react-i18next";
import { getExceptionTypeText } from '../../../utils/i18nUtils';
import { formatStopNumber } from '../../../utils/stopNumberUtils';

// 获取实效性显示文本和颜色
interface AgingLevelInfo {
    text: string | null;
    color: string | null;
}

const getAgingLevelInfo = (agingLevel?: number): AgingLevelInfo => {
    if (!agingLevel || agingLevel === 0 || agingLevel === 1) {
        return { text: null, color: null };
    }
    switch (agingLevel) {
        case 2:
            return { text: '> 24h', color: '#F1C024' }; // 黄色
        case 3:
            return { text: '> 48h', color: '#FE8B00' }; // 橙色
        case 4:
            return { text: '> 72h', color: '#FF1900' }; // 红色
        case 5:
            return { text: '> 120h', color: '#6A00FF' }; // 紫色
        default:
            return { text: null, color: null };
    }
};

export interface DeliveringOrder {
    id: number;
    trackingNumber:  string;
    deliveryAttempts: number;
    stopNumber:  number;
    subStopNumber?: string | null;
    fullName: string;
    address: string;
    standardizedAddress?: string;
    routeNumber: string;
    expectedDeliveryTime: string;
    deliveryType:string;
    deliveredAt: string;
    phone:string;
    exceptionReason?:ExceptionDictionaryType;
    isPickUp?:boolean;
    isAbnormal?:boolean;
    isDelivered?:boolean;
    isDelivery?:boolean;
    isReturnToWhsScanned?:boolean;
    isFinished?:boolean;
    agingLevel?: number;
    taskDate?: number | null;
    addressGroupKey?: string | null;
}

interface OrderCardProps {
    order: DeliveringOrder;
    activeTab?: string;
    hideAddress?: boolean;
    isGrouped?: boolean;
    groupOrderIdList?: number[];
}

const OrderCardTemporary: React.FC<OrderCardProps> = ({order, activeTab, hideAddress = false, isGrouped = false, groupOrderIdList}) => {
    const navigate = useNavigate();
    const {i18n} = useTranslation();
    const [exceptionReasonMsg,setExceptionReasonMsg] = useState<string>('');
    const handleCardClick = () => {
        // 保存主容器的滚动位置
        const mainContainer = document.getElementById('delivering-main-container') as HTMLElement;
        if (mainContainer) {
            sessionStorage.setItem('deliveringPageScrollTop', mainContainer.scrollTop.toString());
        }

        const displayAddress = isGrouped
            ? (order.standardizedAddress || order.address)
            : order.address;
        
        navigate("/delivery/delivering/temporary/:trackingNumber".replace(':trackingNumber', order.trackingNumber),{
            state:{
                deliveryTaskOrderId: order.id,
                trackingNumber: order.trackingNumber,
                activeTab: activeTab,
                orderIdList: groupOrderIdList,
                displayAddress: displayAddress
            }
        });
    };
    useEffect(() => {
        const exceptionReason = order.exceptionReason;
        if(exceptionReason){
            const reasonMsg = getExceptionTypeText(exceptionReason);
            setExceptionReasonMsg(reasonMsg);
        }else {
            setExceptionReasonMsg("")
        }
    }, [order,activeTab]);

    const agingLevelInfo = getAgingLevelInfo(order.agingLevel);

    // stop / stopNumber 颜色跟随实效性；无实效性时保持默认样式
    const stopColorStyle = agingLevelInfo.color ? { color: agingLevelInfo.color } : undefined;

    // 实效性展示组件（根据agingLevel显示不同颜色）
    const AgingLevelBadge = agingLevelInfo.text ? (
        <div 
            className="text-white text-xs px-2 py-1 rounded-full whitespace-nowrap flex items-center gap-1 mt-1"
            style={{ backgroundColor: agingLevelInfo.color || '#FF1900' }}
        >
            {/* 白底圆形，只显示黑色指针，不要外圈 */} 
            <div
                style={{
                    position: 'relative',
                    width: '14px',
                    height: '14px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                }}
            >
                {/* 时针 - 指向3点方向（90度），稍短稍粗 */}
                <span
                    style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        width: '1.5px',
                        height: '4px',
                        backgroundColor: 'black',
                        transform: 'translate(-50%, -100%) rotate(90deg)',
                        transformOrigin: 'bottom center',
                    }}
                />
                {/* 分针 - 指向12点方向（0度），稍长稍细 */}
                <span
                    style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        width: '1px',
                        height: '5px',
                        backgroundColor: 'black',
                        transform: 'translate(-50%, -100%) rotate(0deg)',
                        transformOrigin: 'bottom center',
                    }}
                />
            </div>
            <span className="text-white">{agingLevelInfo.text}</span>
        </div>
    ) : null;

    return (
        <Card 
            className="my-2 rounded-lg" 
            onClick={handleCardClick}
        >
            {!isGrouped && (
                <>
                    <div className="flex justify-between items-center">
                        <div className="text-base font-medium break-all">{order.trackingNumber}</div>
                        <div className="flex items-center gap-2">
                            {order.deliveryAttempts >= 1 && (
                                <Badge
                                    content={order.deliveryAttempts > 99 ? '99+' : order.deliveryAttempts}
                                    className="bg-orange-600 rounded-full shrink-0"
                                >
                                    <div className="size-2 shrink-0" />
                                </Badge>
                            )}
                        </div>
                    </div>
                    <Divider/>
                </>
            )}
            <div className="flex gap-6">
                <div className="flex flex-col min-w-14 max-w-32 items-center justify-center gap-0.5">
                    {(() => {
                        const stopNumber = formatStopNumber(order.stopNumber, order.subStopNumber);
                        return (
                            <div
                                className={`${stopNumber.length > 4 ? 'text-2xl' : 'text-3xl'} font-bold whitespace-nowrap`}
                                style={stopColorStyle}
                            >
                                {stopNumber}
                            </div>
                        );
                    })()}
                    {i18n.language?.startsWith('es') ? (
                        <div className="text-xs text-center">
                            <span className="block">Parada de</span>
                            <span className="block">entrega</span>
                        </div>
                    ) : (
                        <div className="text-xs break-words text-center">
                            {t('pages.stop')}
                        </div>
                    )}
                    {AgingLevelBadge}
                </div>

                <div className="flex flex-col gap-1 flex-1 min-w-0">
                    {isGrouped && (
                        <div className="flex items-center flex-nowrap min-w-0 pt-5 pr-1 pl-0 gap-2">
                            <div className="text-base font-bold text-black whitespace-nowrap min-w-0 flex-1 overflow-x-auto overflow-y-hidden">
                                {order.trackingNumber}
                            </div>
                            {order.deliveryAttempts >= 1 && (
                                <Badge
                                    content={order.deliveryAttempts > 99 ? '99+' : order.deliveryAttempts}
                                    className="bg-orange-600 rounded-full shrink-0 ml-auto mr-0.8"
                                >
                                    <div className="size-2 shrink-0" />
                                </Badge>
                            )}
                        </div>
                    )}
                    <h3 className="text-base font-semibold break-all min-w-0">{order.fullName}</h3>
                    {!hideAddress && <p className="break-words min-w-0">{order.address}</p>}
                    <p>{t('pages.delivery.orderDetail.route')}: {order.routeNumber}</p>
                    {activeTab === 'undelivered' && <p className="text-blue-400">{exceptionReasonMsg || ''}</p>}
                </div>
            </div>

        </Card>
    );
};

export default OrderCardTemporary;